import surveyService from 'services/survey-service';
import highBloodPressureType from 'enums/high-blood-pressure-type';
import pregnancyDiabetType from "enums/pregnancy-diabet-type";
import diagnosisStatus from "enums/diagnosis-status";
import smokingStatus from 'enums/smoking-status';
import cholesterolLevel from "enums/cholesterol-level";
import triglycerideLevel from "enums/triglyceride-level";
import jwtDecode from "jwt-decode";
import _ from "lodash";

const defaultSurvey = {
  id: null,
  reportGenerated: false,
  lastStep: 0,

  birthDate: null,

  address: {},
  gender: null,
  ethnicities: [],

  completedBy: null,

  parentsDiabet: {
    isMother: false,
    isFather: false,
    isDontKnow: false,
    isNeither: true
  },

  diabetic: {
    isPrediabetic: false,
    isDiabetic: false,
    isNeverTold: false
  },

  careProvider: {
    isDiet: false,
    isLifestyle: false,
    isNoChanges: false
  },

  bloodPressure: {
    diastolic: null,
    systolic: null,
    toldBloodPressureType: highBloodPressureType.No,
    toldHighMonth: null,
    toldHighYear: null,
    isOnMedication: false
  },

  bloodSugar: {
    toldIsElevated: false,
    diabetesHistory: {
      toldNoDiabetes: true,
      toldType1Diabetes: false,
      toldType2Diabetes: false,
      toldPreDiabetes: false,
      hadDiabetesType1: false,
      hadDiabetesType2: false,
      hadType2Remission: false,
    },
    isOnMedication: false,
    diabeticDiseases: {
      hasNone: true,
      hasDiabeticEyeDisease: false,
      hasDiabeticKidneyDisease: false,
      hasDiabeticUrineProtein: false,
      hasDiabeticNumbness: false
    }
  },

  arteryBlockages: {
    relativeHadHeartAttack: false,
    blockageHistory: {
      hadNoPlaque: true,
      hadCoronaryPlaque: null,
      hadCarotidPlaque: null,
      hadLegPlaque: null
    },
    blockageTests: {
      hadNoTest: true,
      hadCacTest: null,
      hadHeartAngiogramTest: null,
      hadCtaTest: null,
      hadCimtTest: null,
      hadLegAngiogram: null,
      hadAnkleBrachialTest: null,
      hadCarotidAngiogramTest: null
    },
    lastCacTest: {
      obtainedScore: null,
      doneAtAge: null,
      isProgression: false
    },
    lastHeartAngiogram: {
      hasArteriesBlockage: false,
      doneAtAge: null,
      isProgression: false
    },
    lastCtAngiogram: {
      hasArteriesBlockage: false,
      doneAtAge: null,
      isProgression: false
    },
    lastCimtTest: {
      hasArteriesBlockage: false,
      doneAtAge: null,
      isProgression: false
    },
    lastLegAngiogram: {
      hasArteriesBlockage: false,
      doneAtAge: null,
      isProgression: false
    },
    lastAnkleBrachialTest: {
      hadLowPressureRatio: false,
      reportedLowPressure: false,
      doneAtAge: null,
      isProgression: false
    },
    lastAngiogramCarotidTest: {
      hasArteriesBlockage: false,
      doneAtAge: null,
      isProgression: false
    },
    arteryBlockage: {
      hadArteryBlockage: false,
      hadNoSymptoms: true,
      hadAngina: null,
      hadUnstableAngina: null,
      hadClaudation: null,
      symptomDates: []
    },
    strokes: {
      hadNoStroke: true,
      hadTIA: null,
      hadCVA: null,
      strokeDates: []
    },
    heartAttacks: {
      hadNoHeartAttack: true,
      hadMI: null,
      hadSilentHA: null,
      heartAttackDates: []
    },
    blockedHeartArteryProcedures: {
      hadNoSurgery: true,
      hadBalloonOpening: null,
      hadStent: null,
      hadBypassSurgery: null,
      balloonOpeningDates: [],
      stentDates: [],
      bypassSurgeryDates: []
    },
    blockedLegArteryProcedures: {
      hadNoSurgery: true,
      hadBalloonOpening: null,
      hadStent: null,
      hadBypassSurgery: null,
      balloonOpeningDates: [],
      stentDates: [],
      bypassSurgeryDates: []
    },
    blockedNeckArteryProcedures: {
      hadNoSurgery: true,
      hadSurgery: null,
      hadStent: null,
      blockageSurgeryDates: [],
      stentDates: []
    }
  },

  bloodBiochemistry: {
    cholesterol: {
      highestCholesterolLevel: cholesterolLevel.Unknown,
      highestCholesterolRange: null,
      isOnMedicationToLowerLDL: false,
      isOnMedicationDuringASCVDToLowerLDL: false,
      isOnStatinToLowerLDL: false,
      hadTest: false,
      testScore: null,
      lowestCholesterolLevel: cholesterolLevel.Unknown
    },
    hypercholesterolemia: {
      hadDiagnosis: false,
      hadPositiveTest: false,
      hadThickAchilles: null,
      hadXanthomas: null,
      hadCornealArcus: null,
      familyDiagnoses: {
        hadEarlyHighLDLC: diagnosisStatus.Unknown,
        hadHighLDLC: diagnosisStatus.Unknown,
        hadThickAchilles: diagnosisStatus.Unknown,
        hadXanthomas: diagnosisStatus.Unknown,
        hadCornealArcus: diagnosisStatus.Unknown,
        isNoneDiagnosed: true,
        isUnknown: null,
        isMotherDiagnosed: null,
        isFatherDiagnosed: null,
        isSiblingDiagnosed: null,
        isChildrenDiagnosed: null,
        isMaternalGrandparentDiagnosed: null,
        isPaternalGrandparentDiagnosed: null
      }
    },
    triglycerides: {
      highestTriglycerideLevel: triglycerideLevel.Unknown,
      isOnNoMedication: true,
      isOnHDLCMedication: null,
      isOnTriglyceridesMedication: null,
      isNotTakingAnything: null,
      isTakingFishOilPrescription: null,
      isTakingFishOilSupplement: null,
      isTakingVascepaPrescription: null
    },
    apolipoproteinB: {
      hadTest: false,
      testScore: null
    },
    lipoproteinA: {
      hadTest: false,
      testScore: null,
      unitOfMesure: null
    }
  },

  smoking: {
    smokingStatus: smokingStatus.NotSmoking,
    isSmokeless: null,
    isUsingSmokeless: false,
    yearsUsingSmokeless: null,
    packsPerDayCurrently: null,
    yearsSmokingCurrently: null,
    packsPerDayInPast: null,
    yearsSmokedInPast: null,
    yearStoppedSmoking: null
  },

  medicalConditions: {
    hasMetabolicSyndrome: false,
    conditions: {
      hasNone: true,
      hasCHF: null,
      hasAFIB: null,
      hasFattyLiver: null,
      hasAorticAneurysm: null,
      noConditionTold: true,
      hasLVHypertrophy: null,
      hasSleepApnea: null
    },
    chronicKidneyDisease: {
      hasCKD: false,
      isCKDInStage3Or4: false,
      creatinineLevel: null
    },
    chronicInflammatory: {
      noCondition: true,
      hasInflamation: null,
      hasPsoriasis: null,
      hasRA: null,
      hasAS: null,
      hasHIV: null,
      hasSEL: null
    },
    cReactiveProtein: {
      wasChecked: false,
      highestScore: null
    },
    glycA: {
      wasChecked: false,
      highestScore: null
    },
    femaleConditions: {
      noCondition: true,
      hasEclampsia: null,
      hasEOM: null
    },
    hadCovid19: false,
    hasErectileDysfunction: false
  },

  feedback: {
    informedCMR: null,
    hcpChangesCMR: {
      noChanges: false,
      diet: false,
      lifestyle: false,
      medical: false,
    },
    followchangesCMR: null,
    incontrolCMR: null,
  },

  medicationStatus: null,
  pregnancyDiabetStatus: pregnancyDiabetType.NotApplicable,
  moreInformedStatus: null,
  easyToFollowChangesStatus: null,

  clientName: null,
  billingMethod: null,

  reportInvoice: {},
  lifestyleFactors: {
    exerciseRating: null,
    mentalRating: null,
    nutritionalRating: null,
    physicalRating: null,
    sleepRating: null,
    stressManagementRating: null
  }
}

const state = {
  survey: { ...defaultSurvey },
  originalSurvey: { ...defaultSurvey }
};

const mutations = {
  ["get-survey"](state, survey) {
    if (survey.isCompleted) {
      if (survey.address.address1 && survey.address.city && survey.address.state && survey.address.zip) {
        survey.lastStep = 1;
      }
      else {
        survey.lastStep = 0;
      }
    }

    state.survey = survey;
    state.originalSurvey = _.cloneDeep(survey);
  },
  ["update-survey"](state) {
    const tokenData = jwtDecode(localStorage.getItem("tokenData"));

    if (state.survey.isCompleted && !tokenData.report_to_edit_id)
      state.survey.id = 0;
  },
  ["charge-survey"]() {
  }
};

const actions = {
  async get({ commit }, id) {
    const survey = await surveyService.get(id);
    commit("get-survey", survey);
  },

  async getByReportId({ commit }, id) {
    commit("get-survey", await surveyService.getByReportId(id));
  },

  async update({ commit, state }, { regenerateReport, cancel } = { regenerateReport: false, cancel: false }) {
    await surveyService.update(cancel ? state.originalSurvey : state.survey, regenerateReport);
    commit("update-survey");
  },

  async charge({ commit, state }, data) {
    await surveyService.charge(state.survey.id, data);
    commit("charge-survey");
  }
};

export default {
  namespaced: true,
  state,
  actions,
  mutations
};